import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, App, Button, Card, Result, Space, Spin, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import AdHocTransferForm from '@/components/transfer/AdHocTransferForm';
import { vendorApi } from '@/utils/vendorApi';
import {
  AdHocAcceptedFile,
  AdHocRejectedFile,
  AdHocTransferRequest,
  AdHocTransferResponse,
} from '@/types';

const { Title, Text, Paragraph } = Typography;

function filePathCell(file: string | undefined, transferId?: string | null) {
  const path = file || '—';
  const style = { fontFamily: 'monospace', whiteSpace: 'pre-wrap' as const, wordBreak: 'break-all' as const };
  if (!transferId) {
    return <span style={style}>{path}</span>;
  }
  return (
    <Link to={`/transfers/detail/${encodeURIComponent(transferId)}`} style={style}>
      {path}
    </Link>
  );
}

const viewDetailsColumn = {
  title: '',
  key: 'details',
  width: 120,
  render: (_value: unknown, row: { transfer_id?: string | null }) =>
    row.transfer_id ? (
      <Link to={`/transfers/detail/${encodeURIComponent(row.transfer_id)}`}>View details</Link>
    ) : null,
};

const ACCEPTED_COLUMNS: ColumnsType<AdHocAcceptedFile> = [
  {
    title: 'File',
    dataIndex: 'file',
    key: 'file',
    render: (file: string | undefined, row) => filePathCell(file, row.transfer_id),
  },
  viewDetailsColumn,
];

const REJECTED_COLUMNS: ColumnsType<AdHocRejectedFile> = [
  {
    title: 'File',
    dataIndex: 'file',
    key: 'file',
    render: (file: string | undefined, row) => filePathCell(file, row.transfer_id),
  },
  { title: 'Error', dataIndex: 'error_code', key: 'error_code', width: 220 },
  { title: 'Message', dataIndex: 'message', key: 'message' },
  viewDetailsColumn,
];

function resultStatus(status: AdHocTransferResponse['status']): 'success' | 'warning' | 'error' {
  if (status === 'accepted') {
    return 'success';
  }
  if (status === 'partial') {
    return 'warning';
  }
  return 'error';
}

type ResubmitState =
  | { phase: 'none' }
  | { phase: 'loading' }
  | { phase: 'ready'; vendorId: string; file: string; reason: string }
  | { phase: 'blocked' };

function resubmitRequestMessage(errorCode: string | undefined, fallback: string): string {
  switch (errorCode) {
    case 'UNKNOWN_TRANSFER':
      return 'The original rejected request could not be found.';
    case 'NOT_RESUBMITTABLE':
      return 'This request was already resubmitted.';
    case 'INVALID_REQUEST':
      return 'Only one file can be resubmitted at a time.';
    default:
      return fallback;
  }
}

function resultTitle(result: AdHocTransferResponse): string {
  const acceptedCount = result.accepted?.length ?? 0;
  if (result.status === 'accepted') {
    return `Transfer Accepted for ${acceptedCount} ${acceptedCount === 1 ? 'File' : 'Files'}`;
  }
  if (result.status === 'partial') {
    const rejectedCount = result.rejected?.length ?? 0;
    return `Partial: ${acceptedCount} Accepted, ${rejectedCount} Rejected`;
  }
  return 'Transfer Rejected';
}

function resultSubtitle(result: AdHocTransferResponse): string {
  if (result.status !== 'rejected') {
    return 'Accepted means queued for transfer (async dispatch), not yet delivered to the vendor.';
  }
  const rows = result.rejected ?? [];
  if (rows.length === 1) {
    return rows[0].message || result.message || 'The request was rejected before any files were dispatched.';
  }
  if (rows.length > 1) {
    return `${rows.length} files were rejected. See the details below.`;
  }
  return result.message || 'The request was rejected before any files were dispatched.';
}

const AdHocTransferPage: React.FC = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resubmitOf = (searchParams.get('resubmitOf') || '').trim();
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState<AdHocTransferResponse | null>(null);
  const [resubmit, setResubmit] = useState<ResubmitState>(
    resubmitOf ? { phase: 'loading' } : { phase: 'none' }
  );

  useEffect(() => {
    if (!resubmitOf) {
      setResubmit({ phase: 'none' });
      return;
    }
    let cancelled = false;
    setResubmit({ phase: 'loading' });
    vendorApi.getTransfer(resubmitOf).then(({ transfer }) => {
      if (cancelled) {
        return;
      }
      if (transfer.status === 'rejected') {
        setResubmit({
          phase: 'ready',
          vendorId: transfer.vendorId || '',
          file: transfer.source || '',
          reason: transfer.errorMessage || '',
        });
        return;
      }
      setResubmit({ phase: 'blocked' });
    }).catch(() => {
      if (!cancelled) {
        setResubmit({ phase: 'blocked' });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [resubmitOf]);

  const handleSubmit = async (body: AdHocTransferRequest) => {
    const resubmitting = resubmit.phase === 'ready' && !!resubmitOf;
    const request: AdHocTransferRequest = resubmitting
      ? { ...body, resubmit_of: resubmitOf }
      : body;
    setSubmitting(true);
    setLastResult(null);
    try {
      const result = await vendorApi.triggerAdHocTransfer(request);
      const acceptedCount = result.accepted?.length ?? 0;
      const rejectedCount = result.rejected?.length ?? 0;
      if (result.status === 'accepted' && resubmitting) {
        const nextId = result.accepted?.[0]?.transfer_id;
        message.success(
          `Transfer accepted for ${acceptedCount} file${acceptedCount === 1 ? '' : 's'} (async dispatch — not yet delivered).`
        );
        if (nextId) {
          navigate(`/transfers/detail/${encodeURIComponent(nextId)}`);
          return;
        }
        setLastResult(result);
        return;
      }
      setLastResult(result);
      if (result.status === 'accepted') {
        message.success(
          `Transfer accepted for ${acceptedCount} file${acceptedCount === 1 ? '' : 's'} (async dispatch — not yet delivered).`
        );
      } else if (result.status === 'partial') {
        message.warning(`Partial: ${acceptedCount} accepted, ${rejectedCount} rejected.`);
      } else {
        const fallback = result.message || result.error_code || 'Transfer was rejected.';
        const requestLevel = (result.rejected?.length ?? 0) === 0;
        const errorText = resubmitting && requestLevel
          ? resubmitRequestMessage(result.error_code, fallback)
          : fallback;
        if (resubmitting && requestLevel) {
          setLastResult({ ...result, message: errorText });
        }
        message.error(errorText);
      }
    } catch {
      const errorMessage = 'Failed to start ad hoc transfer';
      setLastResult({
        status: 'rejected',
        vendor_id: body.vendor_id,
        pattern: body.pattern,
        message: errorMessage,
        accepted: [],
        rejected: [],
      });
      message.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const accepted = lastResult?.accepted ?? [];
  const rejected = lastResult?.rejected ?? [];
  const hasFileRows = accepted.length > 0 || rejected.length > 0;

  return (
    <div style={{
      padding: 24,
      width: '100%',
      maxWidth: lastResult && !submitting ? 960 : 720,
      margin: '0 auto',
    }}>
      <Title level={2} style={{ marginTop: 0, marginBottom: 0 }}>
        Ad Hoc Transfer
      </Title>
      <Text type="secondary">
        Send a file to a vendor. Once you start a transfer, it's queued and sent securely
        in the background — you don't need to wait on this page.
      </Text>

      {submitting && (
        <Card style={{ marginTop: 16, textAlign: 'center', padding: '32px 16px' }}>
          <Spin size="large" />
          <Paragraph style={{ marginTop: 24, marginBottom: 0 }}>
            Submitting transfer… this can take a few seconds while the file is encrypted
            and moved to the vendor. Please don't navigate away.
          </Paragraph>
        </Card>
      )}

      {!submitting && lastResult && (
        <Result
          status={resultStatus(lastResult.status)}
          title={resultTitle(lastResult)}
          subTitle={resultSubtitle(lastResult)}
          extra={
            <Space>
              <Button type="primary" onClick={() => setLastResult(null)}>
                Start another transfer
              </Button>
              <Button onClick={() => navigate('/transfers')}>Back to Transfers</Button>
            </Space>
          }
        >
          {hasFileRows && (
            <div style={{ textAlign: 'left' }}>
              {accepted.length > 0 && (
                <div style={{ marginBottom: rejected.length > 0 ? 24 : 0 }}>
                  <Text strong>Accepted files</Text>
                  <Table<AdHocAcceptedFile>
                    size="small"
                    style={{ marginTop: 8 }}
                    rowKey={(row, index) => row.transfer_id || row.file || String(index)}
                    columns={ACCEPTED_COLUMNS}
                    dataSource={accepted}
                    pagination={false}
                  />
                </div>
              )}
              {rejected.length > 0 && (
                <div>
                  <Text strong>Rejected Files</Text>
                  <Table<AdHocRejectedFile>
                    size="small"
                    style={{ marginTop: 8 }}
                    rowKey={(row, index) => row.file || row.error_code || String(index)}
                    columns={REJECTED_COLUMNS}
                    dataSource={rejected}
                    pagination={false}
                  />
                </div>
              )}
              {[...accepted, ...rejected].some((row) => row.transfer_id) ? (
                <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
                  Open a file above to view its status and details.
                </Paragraph>
              ) : null}
            </div>
          )}
        </Result>
      )}

      {!submitting && !lastResult && (
        <Card style={{ marginTop: 16 }}>
          {resubmit.phase === 'loading' ? (
            <div style={{ textAlign: 'center', padding: '32px 16px' }}>
              <Spin />
            </div>
          ) : (
            <>
              {resubmit.phase === 'ready' ? (
                <Alert
                  style={{ marginBottom: 16 }}
                  type="info"
                  showIcon
                  message={`Resubmitting a rejected request. Reason: ${resubmit.reason || '—'}`}
                />
              ) : null}
              {resubmit.phase === 'blocked' ? (
                <Alert
                  style={{ marginBottom: 16 }}
                  type="warning"
                  showIcon
                  message="This rejected request can no longer be resubmitted."
                />
              ) : null}
              <AdHocTransferForm
                key={resubmit.phase === 'ready' ? resubmitOf : 'new'}
                onSubmit={handleSubmit}
                submitting={submitting}
                onCancel={() => navigate('/transfers')}
                initialVendorId={resubmit.phase === 'ready' ? resubmit.vendorId : ''}
                initialFile={resubmit.phase === 'ready' ? resubmit.file : ''}
                singleFile={resubmit.phase === 'ready'}
              />
            </>
          )}
        </Card>
      )}
    </div>
  );
};

export default AdHocTransferPage;
